import { loadAuthToken, clearAuthToken } from 'utils/tokenStorage';
import { routes } from 'utils/routeHelper';
import { handleExpiredToken } from 'utils/session';
import { isJsonString, isNetworkOffline, getDefaultHeaders } from './utils';
import ApiError from './apiError';

const request = async (
  url: string,
  options: { [key: string]: any } = {},
  isRequiredProgram: boolean = true,
  isMarkdown: boolean = false,
): Promise<any> => {
  const requestOptions: any = {
    headers: getDefaultHeaders(),
    ...options,
  };
  let response;
  if (!isRequiredProgram) delete requestOptions.headers.Program;

  if (isNetworkOffline())
    throw new ApiError({ message: 'No internet connection' });

  try {
    response = await fetch(url, requestOptions);
  } catch (e) {
    throw new ApiError({ message: 'Remote API server did not respond' });
  }

  const responseText = await response.text();

  if (
    !response.ok &&
    [
      routes.FORGOT_PASSWORD,
      routes.MFA_REGISTER,
      routes.MFA_VALIDATION,
    ].includes(window.location.pathname)
  ) {
    clearAuthToken();
    const error = isJsonString(responseText) ? JSON.parse(responseText) : {};
    throw new ApiError({
      ...error,
      status: response.status,
    });
  }

  switch (response.status) {
    case 200:
    case 201:
    case 204: {
      if (isMarkdown) return responseText;
      return isJsonString(responseText) ? JSON.parse(responseText) : {};
    }
    case 401: {
      if (window.location.pathname !== routes.LOG_IN) {
        handleExpiredToken();
        return {};
      }
      throw new ApiError({
        status: response.status,
        statusText: response.statusText,
      });
    }
    case 403: {
      throw new ApiError({
        status: response.status,
        statusText: response.statusText,
      });
    }
    default: {
      const error = isJsonString(responseText)
        ? JSON.parse(responseText)
        : { errors: [], message: 'Server error' };
      throw new ApiError({
        status: response.status,
        statusText: response.statusText,
        message: error.message,
        errors: error.errors,
        details: error.details,
      });
    }
  }
};

const authRequest = (
  url: string,
  options: { [key: string]: any } = {},
  isRequiredProgram: boolean = true,
): Promise<any> => {
  const token = loadAuthToken();
  if (token == null || token === '') {
    throw new Error('Undefined Token');
  }
  const jwtToken = `JWT ${loadAuthToken()}`;
  const authenticatedRequestOptions = {
    ...options,
    headers: {
      ...getDefaultHeaders(),
      Authorization: jwtToken,
      ...options.headers,
    },
  };
  return request(url, authenticatedRequestOptions, isRequiredProgram);
};

export default {
  request,
  authRequest,
};
